import { useCallback, useMemo, useState } from 'react';
import { BankIdData, BankIdInitData, ParticipantProps } from '../../../utils/types';
import axios from 'axios';
import { formatPhoneNumber, formatSimpleaDate, getBaseUrl, parseJson } from '../../../utils';
import { validateYupSchema, yupToFormErrors } from 'formik';
import { validationSchemaDefault } from './validations';
import dayjs from 'dayjs';
import { idTypeStaticMapper, type IdCardType } from './utils';

export enum BankIdConfirmationFormFields {
    TitleBefore = 'titleBefore',
    TitleAfter = 'titleAfter',
    Email = 'email',
    Phone = 'phone',
    PhonePrefix = 'phonePrefix',
    BirthPlace = 'birthPlace',
    BirthCountry = 'birthCountry',
    Citizenship = 'citizenship',
    SecondCitizenship = 'secondCitizenship',
    ValidateSecondCitizenship = 'validateSecondCitizenship',
    IssueDate = 'issueDate',
    Issuer = 'issuer',
    BankAccountCode = 'bankAccountCode',
    BankAccountNo = 'bankAccountNo',
    BankAccountPrefix = 'bankAccountPrefix',
    IdCardCountry = 'idCardCountry',
}

type ModelRequestBodyType = Omit<BankIdData, 'BankIdIdentificationInitData'>;

export type BankIdConfirmationFormData = {
    [key in BankIdConfirmationFormFields]?: string | undefined;
};

export type BankIDDataType = Omit<BankIdConfirmationFormData, 'validateSecondCitizenship'> & {
    validateSecondCitizenship: boolean;
};

export const useBankIdConfirmation = (response: BankIdData) => {
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPep, setIsPep] = useState(false);
    const [isIdentificationSuccessful, setIsIdentificationSuccessful] = useState(false);

    const participant: ParticipantProps = parseJson(response.ClientPersonDtoJson);

    const initData: BankIdInitData = parseJson(response.BankIdIdentificationInitData);
    const idCardCountryCode = participant?.IdCards[0]?.IdCardCountryCode;
    const idCardKindCode = participant?.IdCards[0]?.IdCardKindCode;

    const idTypeTranslated =
        initData &&
        initData?.IdCardConfigurations.find(
            (id) =>
                participant.IdCards[0] &&
                id.Code === participant.IdCards[0]?.IdCardKindCode + participant.IdCards[0]?.IdCardCountryCode
        )?.DisplayNameTranslated;

    const idType =
        !idCardCountryCode && idCardKindCode ? idTypeStaticMapper[idCardKindCode as IdCardType] : idTypeTranslated;

    const countries = useMemo(
        () =>
            initData?.Lovs.Countries.map(({ NameTranslated, Code }) => ({
                label: NameTranslated,
                value: Code,
            })),
        [initData?.Lovs.Countries]
    );

    const banks = useMemo(
        () => [
            { label: 'Vyberte...', value: '' }, // Prázdná hodnota
            ...(initData?.Banks || [])
                .map(({ BankCode, Name }) => ({
                    label: BankCode + ' - ' + Name,
                    value: BankCode,
                }))
                .sort((a, b) => Number(a.value) - Number(b.value)),
        ],
        [initData?.Banks]
    );

    const prefixes = useMemo(
        () =>
            initData?.InternationalPhonePrefixes.map(({ PhonePrefix }) => ({
                label: PhonePrefix,
                value: PhonePrefix,
            })),
        [initData?.InternationalPhonePrefixes]
    );

    const lovs = {
        countries,
        banks,
        prefixes,
    };

    const issueDate = participant?.IdCards[0]?.IssuedDt;
    const hasIssueDate = issueDate && issueDate !== null;

    const onSubmit = useCallback(
        (values: BankIDDataType) => {
            setIsSubmitting(true);
            const { BankIdIdentificationInitData, ...restOfResponse } = response;

            const ClientPersonDtoJson: ParticipantProps = {
                ...participant,
                IdCards: [
                    {
                        ...participant.IdCards[0],
                        IssuedDt: hasIssueDate
                            ? issueDate
                            : values.issueDate
                              ? formatSimpleaDate(dayjs(values.issueDate).toDate())
                              : null,
                        Issuer: values.issuer || '',
                        IdCardCountryCode: idCardCountryCode ? idCardCountryCode : values.idCardCountry || '',
                    },
                ],
                PersonalIdentification: {
                    ...participant.PersonalIdentification,
                    BirthCountryCode: values.birthCountry ?? null,
                    BirthDt: participant.PersonalIdentification.BirthDt,
                    Birthplace: values.birthPlace || participant.PersonalIdentification.Birthplace,
                    CitizenshipCode: values.citizenship || '',
                    SecondCitizenshipCode: values.secondCitizenship || null,
                    DeserializationCode: participant.PersonalIdentification.DeserializationCode,
                    TitleBefore: values.titleBefore || '',
                    TitleAfter: values.titleAfter || '',
                },
                BankAccounts: [
                    {
                        BankAccountNo: values.bankAccountNo || '',
                        BankCode: values.bankAccountCode || '',
                        Prefix: values.bankAccountPrefix || '',
                        Iban: '',
                    },
                ],
                Contact: {
                    Email: values.email || '',
                    Phone: values.phone || '',
                    PhonePrefix: {
                        ...participant.Contact.PhonePrefix,
                        Prefix: values.phonePrefix || '',
                    },
                },
                IsPep: isPep,
            };

            const body: ModelRequestBodyType = {
                ...restOfResponse,
                ClientPersonDtoJson: JSON.stringify(ClientPersonDtoJson),
            };

            axios
                .post(`${getBaseUrl()}/api/client-aml-identification/confirm-identification-data-by-bank-id`, body, {
                    timeout: 10000,
                })
                .then((response) => {
                    if (response.status === 200) {
                        setIsIdentificationSuccessful(true);
                    }
                })
                .catch((error: any) => {
                    console.error('err: ', error);
                });
        },
        [isPep, participant, response, idCardCountryCode]
    );

    const handleValidate = useCallback(
        (values: BankIDDataType) => {
            !submitted && setSubmitted(true);
            try {
                validateYupSchema(values, validationSchemaDefault(!!issueDate), true, values);
            } catch (err) {
                return yupToFormErrors(err);
            }
            return {};
        },
        [issueDate, submitted]
    );

    const prefixesList = initData?.InternationalPhonePrefixes.map((prefix) => prefix.PhonePrefix);

    const isSupportedPrefix = prefixesList?.includes(participant.Contact.PhonePrefix.Prefix);

    const phoneNumber =
        participant?.Contact.Phone && participant?.Contact.PhonePrefix
            ? `${participant?.Contact.PhonePrefix.Prefix} ${formatPhoneNumber(participant?.Contact.Phone || '')}`
            : '';

    const hasBirthPlace = !!participant?.PersonalIdentification?.Birthplace;

    const hasIssuer = !!participant?.IdCards[0].Issuer && participant.IdCards[0].Issuer.length > 0;

    const hasCitizenship = !!participant?.PersonalIdentification.CitizenshipCode;

    return {
        isPep,
        setIsPep,
        onSubmit,
        participant,
        isSupportedPrefix,
        hasIssuer,
        submitted,
        setSubmitted,
        issueDate,
        hasIssueDate,
        hasCitizenship,
        initData,
        isSubmitting,
        lovs,
        phoneNumber,
        hasBirthPlace,
        handleValidate,
        isIdentificationSuccessful,
        idCardCountryCode,
        idCardKindCode,
        idType,
    };
};
